import React, { useEffect } from "react";
import { useState } from "react";
import { useGridApiRef, GridCellParams, GridRenderCellParams, GridColumnHeaderParams } from "@mui/x-data-grid";
import { DataGridPro, GridApiPro, GridSlotsComponentsProps } from "@mui/x-data-grid-pro"; // TOOD: get license key, trial for now
import { GridColDef } from "@mui/x-data-grid-pro";
import Box from "@mui/material/Box";
import { LoadingButton } from "@mui/lab";
import Paper from "@mui/material/Paper";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import { useTheme } from "@mui/material/styles";
import { deepmerge } from "@mui/utils";

import StockPlan from "./../../StockPlanTypes/StockPlan";
import { Skeleton } from "@mui/material";

import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import Tooltip from "@mui/material/Tooltip";

import AsOf from "../UtilityComponents/PaperUtilityComponents/AsOf";
import IconButton from "@mui/material/IconButton";
import EditIcon from "@mui/icons-material/Edit";

import { FormGroup } from "@mui/material";
import FormControlLabel from "@mui/material/FormControlLabel";
import Switch from "@mui/material/Switch";

import AddIcon from "@mui/icons-material/Add";

import dayjs, { Dayjs } from "dayjs";
import { LinearProgress, Typography } from "@mui/material";
import LearnTextEmbed from "../LearnPanelComponents/LearnCopy/LearnTextEmbed";
import NoWrapTooltip from "../UtilityComponents/TooltipUtilityComponents/NoWrapTooltip";
import { isMobile } from "react-device-detect";

const firstAndLastColumnsListWidth = 180;
const rowNameWidth = 200;
const buttonWidth = 40;

var advancedFormat = require("dayjs/plugin/advancedFormat");
dayjs.extend(advancedFormat);
var quarterOfYear = require("dayjs/plugin/quarterOfYear");
dayjs.extend(quarterOfYear);

const paperPadding = 2;
const bigFontSize = 16;
const mediumFontSize = 14;
const smallFontSize = 12;
const enterToolTipDelay = 400;
const TableFormatStringMap = {
  day: "MMM D, 'YY",
  month: "MMM YYYY",
  quarter: "[Q]Q YYYY",
  year: "YYYY",
};

type GranularityType = "day" | "month" | "quarter" | "year";
const GranularityArray: GranularityType[] = ["day", "month", "quarter", "year"];

const firstAndLastColumnsList = ["Vested Today", "Overall Total"];

const allGrantsString = "All Grants";

const today = dayjs();

type dataType = {
  [key: string]: {
    Shares: number;
    Shares_sold?: number;
    Value: number;
    Shares_withheld: number;
    Value_withheld: number;
    Value_at_vest?: number | null;
    Value_at_vest_withheld?: number | null;
    Display_data_override?: boolean;
    Display_vest_data_override?: boolean;
  };
};

function createDataFromStockPlan(
  stockPlan: StockPlan,
  stockPrice: number,
  vestDateGroups: Dayjs[],
  tableHeaders: string[],
  granularity: GranularityType
) {
  const data: dataType = {};

  //TODO: make this more efficient

  for (let i = 0; i < vestDateGroups.length; i++) {
    //TODO: how to properly handle being in the middle of a quarter  for groups? (should just assume vests at end, I think? or we can use the actual days)
    let curr_shs = 0;
    let curr_value_at_vest = 0;
    let curr_shs_withheld = 0;
    let curr_value_at_vest_withheld = 0;
    let null_out_curr_value_at_vest = false;
    let display_data_override = false;
    let display_vest_data_override = false;
    for (let j = 0; j < stockPlan.stock_vests.length; j++) {
      if (
        // @ts-expect-error
        stockPlan.stock_vests[j].date.isSame(vestDateGroups[i], granularity)
      ) {
        display_data_override = true;
        curr_shs += stockPlan.stock_vests[j].vested_shares_on_date;
        curr_shs_withheld += stockPlan.stock_vests[j].withheld_shares_on_date;
        const vp = stockPlan.stock_vests[j].vest_price;
        if (vp === null) {
          null_out_curr_value_at_vest = true;
          display_vest_data_override = false;
        } else {
          curr_value_at_vest += stockPlan.stock_vests[j].vested_shares_on_date * vp;
          curr_value_at_vest_withheld += stockPlan.stock_vests[j].withheld_shares_on_date * vp;
          display_vest_data_override = true;
        }
      }
    }
    data[tableHeaders[i + 1]] = {
      Shares: curr_shs,
      Value: curr_shs * stockPrice,
      Shares_withheld: curr_shs_withheld,
      Value_withheld: curr_shs_withheld * stockPrice,
      Value_at_vest: null_out_curr_value_at_vest ? null : curr_value_at_vest,
      Value_at_vest_withheld: null_out_curr_value_at_vest ? null : curr_value_at_vest_withheld,
      Display_data_override: display_data_override,
      Display_vest_data_override: display_vest_data_override,
    }; //offset for the first vested today column for now
  }

  let today_shs = 0; // makes the today vested exactly accurate to the day independent of vesting granularity
  let today_value_at_vest: number | null = 0;
  let today_shs_withheld = 0;
  let today_value_at_vest_withheld: number | null = 0;
  for (let i = 0; i < stockPlan.stock_vests.length; i++) {
    if (stockPlan.stock_vests[i].date.isBefore(today, "day")) {
      today_shs += stockPlan.stock_vests[i].vested_shares_on_date;
      today_shs_withheld += stockPlan.stock_vests[i].withheld_shares_on_date;
      const vp = stockPlan.stock_vests[i].vest_price;
      today_value_at_vest =
        today_value_at_vest === null || vp === null
          ? null
          : today_value_at_vest + stockPlan.stock_vests[i].vested_shares_on_date * vp;
      today_value_at_vest_withheld =
        today_value_at_vest_withheld === null || vp === null
          ? null
          : today_value_at_vest_withheld + stockPlan.stock_vests[i].withheld_shares_on_date * vp;
    }
  }

  const unsold_today_shs = today_shs - stockPlan.total_shares_sold;
  const unsold_pct = unsold_today_shs / today_shs;

  data[firstAndLastColumnsList[0]] = {
    Shares: unsold_today_shs,
    Shares_sold: stockPlan.total_shares_sold,
    Value: unsold_today_shs * stockPrice,
    Shares_withheld: today_shs_withheld,
    Value_withheld: today_shs_withheld * stockPrice,
    Value_at_vest: today_value_at_vest === null ? null : today_value_at_vest * unsold_pct,
    Value_at_vest_withheld: today_value_at_vest_withheld,
  };

  data[firstAndLastColumnsList[1]] = {
    Shares: stockPlan.number_shares - stockPlan.total_shares_sold,
    Value: (stockPlan.number_shares - stockPlan.total_shares_sold) * stockPrice,
    Shares_withheld: stockPlan.number_shares_withheld,
    Value_withheld: stockPlan.number_shares_withheld * stockPrice,
  };

  return { name: stockPlan.name, data: data };
}

type rowsType = {
  name: string;
  data: {
    [key: string]: {
      Shares: number;
      Shares_sold?: number;
      Value: number;
      Shares_withheld: number;
      Value_withheld: number;
      Value_at_vest?: number | null;
      Value_at_vest_withheld?: number | null;
      Display_data_override?: boolean;
      Display_vest_data_override?: boolean;
    };
  };
}[];

function createTotalRows(rows: rowsType) {
  const totalData: dataType = {};

  rows.forEach((row) => {
    for (let [k, v] of Object.entries(row.data)) {
      if (!totalData[k]) {
        totalData[k] = {
          Shares: 0,
          Shares_sold: 0,
          Value: 0,
          Shares_withheld: 0,
          Value_withheld: 0,
          Value_at_vest: 0,
          Value_at_vest_withheld: 0,
          Display_data_override: true,
          Display_vest_data_override: undefined,
        };
      }
      totalData[k].Shares += v.Shares;
      totalData[k].Shares_sold =
        totalData[k].Shares_sold === undefined ? undefined : (totalData[k].Shares_sold ?? 0) + (v.Shares_sold || 0);
      totalData[k].Value += v.Value;
      totalData[k].Shares_withheld += v.Shares_withheld;
      totalData[k].Value_withheld += v.Value_withheld;
      totalData[k].Value_at_vest =
        totalData[k].Value_at_vest === null ||
        totalData[k].Value_at_vest === undefined ||
        v.Value_at_vest === null ||
        v.Value_at_vest === undefined
          ? undefined
          : (totalData[k].Value_at_vest || 0) + (v.Value_at_vest || 0);
      totalData[k].Value_at_vest_withheld =
        totalData[k].Value_at_vest_withheld === null ||
        totalData[k].Value_at_vest_withheld === undefined ||
        v.Value_at_vest_withheld === null ||
        v.Value_at_vest_withheld === undefined
          ? undefined
          : (totalData[k].Value_at_vest_withheld || 0) + (v.Value_at_vest_withheld || 0);
      totalData[k].Display_vest_data_override =
        totalData[k].Display_vest_data_override === undefined
          ? v.Display_vest_data_override
          : totalData[k].Display_vest_data_override && v.Display_vest_data_override;
    }
  });

  // If no data, ensure vested today and overall total sum to 0
  firstAndLastColumnsList.forEach((col) => {
    if (!totalData[col]) {
      totalData[col] = {
        Shares: 0,
        Shares_sold: 0,
        Value: 0,
        Shares_withheld: 0,
        Value_withheld: 0,
        Value_at_vest: undefined,
        Value_at_vest_withheld: undefined,
      };
    }
  });

  return [{ name: allGrantsString, data: totalData }];
}

function createCumulativeRows(rows: rowsType) {
  const newRows: rowsType = [];
  rows.forEach((row) => {
    let shsRunningTotal = 0;
    let valRunningTotal = 0;
    let shsWithheldRunningTotal = 0;
    let valWithheldRunningTotal = 0;
    let valueAtVestRunningTotal: number | undefined = 0;
    let valueAtVestWithheldRunningTotal: number | undefined = 0;
    const newData: dataType = {};
    for (let [k, v] of Object.entries(row.data)) {
      if (k === firstAndLastColumnsList[0]) {
        newData[k] = v;
      } else {
        if (k === firstAndLastColumnsList[1]) {
          shsRunningTotal = v.Shares;
          valRunningTotal = v.Value;
          shsWithheldRunningTotal = v.Shares_withheld;
          valWithheldRunningTotal = v.Value_withheld;
          valueAtVestRunningTotal = v.Value_at_vest || undefined;
          valueAtVestWithheldRunningTotal = v.Value_at_vest_withheld || undefined;
        } else {
          shsRunningTotal += v.Shares;
          valRunningTotal += v.Value;
          shsWithheldRunningTotal += v.Shares_withheld;
          valWithheldRunningTotal += v.Value_withheld;
          valueAtVestRunningTotal =
            valueAtVestRunningTotal !== undefined && v.Value_at_vest !== null && v.Value_at_vest !== undefined
              ? valueAtVestRunningTotal + v.Value_at_vest
              : undefined;
          valueAtVestWithheldRunningTotal =
            valueAtVestWithheldRunningTotal !== undefined &&
            v.Value_at_vest_withheld !== null &&
            v.Value_at_vest_withheld !== undefined
              ? valueAtVestWithheldRunningTotal + v.Value_at_vest_withheld
              : undefined;
        }
        newData[k] = {
          Shares: shsRunningTotal,
          Value: valRunningTotal,
          Shares_withheld: shsWithheldRunningTotal,
          Value_withheld: valWithheldRunningTotal,
          Value_at_vest: valueAtVestRunningTotal,
          Value_at_vest_withheld: valueAtVestWithheldRunningTotal,
          Display_data_override: v.Display_data_override,
          Display_vest_data_override: v.Display_vest_data_override,
        };
      }
    }
    newRows.push({ name: row.name, data: newData });
  });

  return newRows;
}

// const firstAndLastColumnsColor = AddOpacity(theme.palette.info.light, 0.5);
// const hoveredFirstAndLastColumnsColor = AddOpacity(theme.palette.info.light, 0.54);

// const totalRowColor = AddOpacity(theme.palette.success.light, 0.7);

// const hoveredTotalRowColor = AddOpacity(theme.palette.success.light, 0.74);

type DetailsTableProps = {
  stockPlans: StockPlan[];
  onStockPlanEdit: (name: string) => void;
  startAdd: () => void;
  stockPrice: number | undefined;
  referenceDateString: string;
  loadingAddStockPlan: boolean;
  loadingTable: boolean;
  withholdingOn: boolean;
  withholdingRate: number;
  firstWithholdingCheckDone: boolean;
  showNet: boolean;
  setShowNet: (newShowNet: boolean | ((oldShowNet: boolean) => boolean)) => void;
};

declare module "@mui/x-data-grid" {
  interface FooterPropsOverrides {
    onClick: () => void;
    loading: boolean;
  }
}

function CustomFooterAddButtonComponent(props: NonNullable<GridSlotsComponentsProps["footer"]>) {
  const [clickedWhileLoading, setClickedWhileLoading] = useState(false); // this displays a loading symbol on the button only if clicked while not ready yet

  const loading = props.loading;
  const onClick = () => {
    props.onClick?.();
  };
  useEffect(() => {
    if (!loading && clickedWhileLoading) {
      setClickedWhileLoading(false);
      onClick();
    }
  }, [loading, setClickedWhileLoading, clickedWhileLoading]);

  return (
    <Box sx={{ display: "flex" }} className="highlight-for-details-tour-step-1">
      <LoadingButton
        onClick={() => {
          if (props.loading) {
            setClickedWhileLoading(true);
          } else if (props.onClick !== undefined) {
            setClickedWhileLoading(false);
            onClick();
          }
        }}
        variant="outlined"
        size="large"
        startIcon={<AddIcon />}
        loading={clickedWhileLoading}
        sx={{ flexGrow: 1 }}
      >
        Add A Stock Plan
      </LoadingButton>
    </Box>
  );
}

function DetailsTable(props: DetailsTableProps) {
  const theme = useTheme();
  // TODO: lift incremental, group dates by setting, and display fields so they don't reset on tab switches
  const [incrementalOrCumulative, setIncrementalOrCumulative] = useState<"Incremental" | "Cumulative">("Cumulative");

  useEffect(() => {
    props.setShowNet(props.withholdingOn);
  }, [props.withholdingOn]);

  const [groupDatesBySetting, setGroupDatesBySetting] = useState<GranularityType>("month");

  // const [hoveredColumn, setHoveredColumn] = useState<string | null>(null);

  const dataGridApiRef = useGridApiRef<GridApiPro>();

  const allVestDates = props.stockPlans
    .map((sp) => sp.stock_vests.map((sv) => sv.date))
    .flat(1)
    .sort(function (a, b) {
      return a.diff(b);
    });

  const uniqueVestDates: Dayjs[] = [];

  let currVestDate = null;

  for (let i = 0; i < allVestDates.length; i++) {
    if (
      currVestDate === null ||
      // @ts-expect-error
      !currVestDate.isSame(allVestDates[i], groupDatesBySetting)
    ) {
      currVestDate = allVestDates[i];
      uniqueVestDates.push(currVestDate);
    }
  }

  const tableHeaderList = [
    firstAndLastColumnsList[0],
    ...uniqueVestDates.map((dt) => dt.format(TableFormatStringMap[groupDatesBySetting])),
    firstAndLastColumnsList[1],
  ];

  const rowsNoTotal = props.stockPlans.map((sp) =>
    createDataFromStockPlan(sp, props.stockPrice || -1, uniqueVestDates, tableHeaderList, groupDatesBySetting)
  );
  const rowsNoTotalCumulative = createCumulativeRows(rowsNoTotal);

  const rowsTotal = createTotalRows(rowsNoTotal);
  const rowsTotalCumulative = createCumulativeRows(rowsTotal);

  // function backgroundColorFromColumnTextAndRowName(text: string, row_name: string = "") {
  //   const isFirstOrLastColumn = firstAndLastColumnsList.includes(text);
  //   const isHoveredColumn = text === hoveredColumn;
  //   const isTotalRow = row_name === allGrantsString;

  //   if (isTotalRow) {
  //     if (isHoveredColumn) {
  //       return { backgroundColor: hoveredTotalRowColor };
  //     } else {
  //       return { backgroundColor: totalRowColor };
  //     }
  //   } else {
  //     if (isFirstOrLastColumn) {
  //       if (isHoveredColumn) {
  //         return { backgroundColor: hoveredFirstAndLastColumnsColor };
  //       } else {
  //         return { backgroundColor: firstAndLastColumnsColor };
  //       }
  //     } else {
  //       if (isHoveredColumn) {
  //         return { backgroundColor: theme.palette.action.hover };
  //       } else {
  //         return {};
  //       }
  //     }
  //   }
  // }

  const sharesExtraConfig = {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  };

  function addBulletAndShareOrShares(shares: number, extra_in_between: string = "", bullet: boolean = true) {
    return (
      (bullet ? "• " : "") +
      shares.toLocaleString("en-US", sharesExtraConfig) +
      extra_in_between +
      (shares === 1 ? " share" : " shares")
    );
  }

  function vestingPrefixText(index: number) {
    return incrementalOrCumulative === "Incremental"
      ? uniqueVestDates[index - 1].isBefore(
          today,
          // @ts-expect-error
          groupDatesBySetting
        )
        ? "vested " + (groupDatesBySetting === "day" ? "on" : "in")
        : "vesting " + (groupDatesBySetting === "day" ? "on" : "in")
      : uniqueVestDates[index - 1].isBefore(
          today,
          // @ts-expect-error
          groupDatesBySetting
        )
      ? "vested by"
      : "vesting by";
  }

  function dateRowCellRender(params: GridRenderCellParams, index: number) {
    const dollarsExtraConfig = {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    };

    let tooltipText = "";
    let firstRow = <div>"-"</div>;
    let secondRow = <div />;

    const currData = params.row.data[params.field];

    if (currData !== undefined) {
      const currShares = currData.Shares + (props.showNet ? 0 : currData.Shares_withheld || 0);
      const soldShares = currData.Shares_sold;
      const totalVestedShares = params.row.data[firstAndLastColumnsList[0]].Shares;
      const totalVestedSharesWithheld = params.row.data[firstAndLastColumnsList[0]].Shares_withheld || 0;
      const totalSoldShares = params.row.data[firstAndLastColumnsList[0]].Shares_sold || 0;
      const currValue = currData.Value + (props.showNet ? 0 : currData.Value_withheld || 0);
      const currValueAtVest =
        (currData.Value_at_vest || 0) + (props.showNet ? 0 : currData.Value_at_vest_withheld || 0);
      if (firstAndLastColumnsList.includes(params.field)) {
        if (params.field === firstAndLastColumnsList[0]) {
          tooltipText +=
            addBulletAndShareOrShares(
              currData.Shares + totalSoldShares + (currData.Shares_withheld || 0),

              " total (gross)",
              false
            ) +
            " vested from " +
            params.row.name +
            ":\n";
          tooltipText += addBulletAndShareOrShares(totalSoldShares) + " sold\n";
          tooltipText += addBulletAndShareOrShares(currData.Shares) + " held\n";
          tooltipText += addBulletAndShareOrShares(currData.Shares_withheld || 0) + " withheld for taxes\n";
          firstRow = (
            <div>
              {currShares.toLocaleString("en-US", sharesExtraConfig) +
                " " +
                "($" +
                (currValue >= 0 ? currValue.toLocaleString("en-US", dollarsExtraConfig) : "-") +
                ")"}
            </div>
          );
          secondRow = (
            <Typography color="text.primary" fontSize={mediumFontSize}>
              at vest:
              {/* {currValueAtVest ? "at vest: $" + currValueAtVest.toLocaleString("en-US", dollarsExtraConfig) : <br />} */}
            </Typography>
          );
        } else {
          tooltipText +=
            addBulletAndShareOrShares(
              currData.Shares + totalSoldShares + (currData.Shares_withheld || 0),
              " total (gross)",
              false
            ) +
            " in " +
            params.row.name +
            ":\n";
          tooltipText += addBulletAndShareOrShares(totalSoldShares) + " sold\n";
          tooltipText += addBulletAndShareOrShares(totalVestedShares) + " held, vested\n";
          tooltipText += addBulletAndShareOrShares(currData.Shares - totalVestedShares) + ", unvested\n";
          tooltipText += addBulletAndShareOrShares(totalVestedSharesWithheld) + " already withheld\n";
          tooltipText +=
            addBulletAndShareOrShares((currData.Shares_withheld || 0) - totalVestedSharesWithheld, " more") +
            " to be withheld\n";
          firstRow = (
            <div>
              {isNaN(currShares) ? (
                <br />
              ) : (
                currShares.toLocaleString("en-US", sharesExtraConfig) +
                " " +
                "($" +
                (currValue >= 0 ? currValue.toLocaleString("en-US", dollarsExtraConfig) : "-") +
                ")"
              )}
            </div>
          );
          const vestedValue =
            params.row.data[firstAndLastColumnsList[0]].Value +
            (props.showNet ? 0 : params.row.data[firstAndLastColumnsList[0]].Value_withheld || 0);
          const totalValue =
            params.row.data[firstAndLastColumnsList[1]].Value +
            (props.showNet ? 0 : params.row.data[firstAndLastColumnsList[1]].Value_withheld || 0);
          const unvestedValue = totalValue - vestedValue ?? undefined;
          secondRow =
            unvestedValue === undefined ? (
              <div>
                <br />
              </div>
            ) : (
              <Typography color="text.primary" fontSize={mediumFontSize}>
                {"$" +
                  (unvestedValue >= 0 ? unvestedValue.toLocaleString("en-US", dollarsExtraConfig) : "-") +
                  " unvested"}
              </Typography>
            );
        }
      } else {
        const displayDataOverride = currData.Display_data_override === true;
        const displayVestDataOverride = currData.Display_vest_data_override === true;
        if (currShares || displayDataOverride) {
          tooltipText +=
            addBulletAndShareOrShares(
              currData.Shares + (currData.Shares_sold || 0) + (currData.Shares_withheld || 0),
              " total (gross)",
              false
            ) +
            " " +
            vestingPrefixText(index) +
            " " +
            tableHeaderList[index] +
            " from " +
            params.row.name +
            ":\n";
          tooltipText += addBulletAndShareOrShares(currData.Shares) + " net\n";
          tooltipText += addBulletAndShareOrShares(currData.Shares_withheld || 0) + " withheld for taxes\n";
        }
        firstRow = (
          <div>
            {currShares || displayDataOverride ? currShares.toLocaleString("en-US", sharesExtraConfig) : <br />}
          </div>
        );
        secondRow = (
          <Typography color="text.secondary" fontSize={mediumFontSize}>
            {currValueAtVest || displayVestDataOverride ? (
              "$" + currValueAtVest.toLocaleString("en-US", dollarsExtraConfig)
            ) : (
              <br />
            )}
          </Typography>
        );
      }
    }

    const boxContents = (
      <Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-end", flexGrow: 1 }}>
        {firstRow}
        {secondRow}
      </Box>
    );

    if (tooltipText) {
      return (
        <NoWrapTooltip
          title={<div style={{ whiteSpace: "pre-line", fontSize: "1rem" }}>{tooltipText}</div>}
          enterDelay={enterToolTipDelay}
          arrow
          placement="bottom"
        >
          {boxContents}
        </NoWrapTooltip>
      );
    }

    return boxContents;
  }

  const removeDefaultGrayBackgroundTheme = createTheme({
    palette: {
      background: {
        default: "#ffffff",
      },
    },
  });
  // Styling for the data table
  const dataGridSX = {
    " & .all-grants-summary-row-label": {
      fontWeight: "bold",
      fontSize: bigFontSize,
    },
    "& .regular-row-label": {
      fontWeight: "bold",
      fontSize: bigFontSize,
      borderBottom: "none",
    },

    "& .all-grants-summary-row-cells": {
      fontSize: bigFontSize,
      fontWeight: "bold",
      color: theme.palette.info.main,
    },
    "& .summary-column-cell": {
      fontSize: bigFontSize,
      color: theme.palette.primary.main,
      borderBottom: "none",
    },
    "& .regular-column-cell": {
      fontSize: mediumFontSize,
      borderBottom: "none",
    },
  }; //TODO: add more classes for already vested and future vesting? (be wary of too many colors)

  // TODO: add no rows overlay

  const dataGridColumns: GridColDef[] = [
    {
      field: "rowNames",
      headerName: "",
      cellClassName: (params: GridCellParams) =>
        params.id === allGrantsString && params.rowNode.type === "pinnedRow"
          ? "all-grants-summary-row-label"
          : "regular-row-label",
      minWidth: rowNameWidth, // 150,
      sortable: false,
      renderCell: (params: GridRenderCellParams) => {
        const isOverallTotalRow = params.id === allGrantsString && params.rowNode.type === "pinnedRow";
        const vestedShares =
          params.row.data[firstAndLastColumnsList[0]].Shares +
          params.row.data[firstAndLastColumnsList[0]].Shares_sold +
          (props.showNet ? 0 : params.row.data[firstAndLastColumnsList[0]].Shares_withheld || 0);
        const totalShares =
          params.row.data[firstAndLastColumnsList[1]].Shares +
          params.row.data[firstAndLastColumnsList[0]].Shares_sold +
          (props.showNet ? 0 : params.row.data[firstAndLastColumnsList[1]].Shares_withheld || 0);
        const pctVested = vestedShares / totalShares;
        return (
          <>
            {params.rowNode.type === "pinnedRow" ? null : (
              <IconButton onClick={() => props.onStockPlanEdit(params.row.name)}>
                <EditIcon />
              </IconButton>
            )}
            <Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-start" }}>
              {params.row.name}
              {isNaN(pctVested) ? (
                <div>
                  <br />
                </div>
              ) : (
                <Box
                  alignItems="center"
                  sx={{ display: "flex", flexDirection: "row", width: rowNameWidth - buttonWidth - 24 }}
                >
                  <Box sx={{ width: "100%", mr: 1 }}>
                    <LinearProgress
                      variant="determinate"
                      value={pctVested * 100}
                      sx={{
                        width: rowNameWidth - buttonWidth - 100,
                        height: 15,
                        border: 0.5,
                        borderRadius: 1,
                        borderColor:
                          pctVested > 0.99
                            ? theme.palette.success.light
                            : isOverallTotalRow
                            ? theme.palette.info.light
                            : theme.palette.primary.light,
                        // borderColor: theme.palette.text.primary,
                        // backgroundColor: theme.palette.text.disabled,
                        backgroundColor: theme.palette.background.default,
                        "& .MuiLinearProgress-bar": {
                          backgroundColor:
                            pctVested > 0.99
                              ? theme.palette.success.main
                              : isOverallTotalRow
                              ? theme.palette.info.main
                              : theme.palette.primary.main,
                        },
                      }}
                    />
                  </Box>
                  <Typography color="text.primary" fontSize={mediumFontSize}>
                    {pctVested.toLocaleString("en-US", { style: "percent", maximumFractionDigits: 0 }) + " vested"}
                  </Typography>
                </Box>
              )}
            </Box>
          </>
        );
      },
      // valueGetter: (params: GridValueGetterParams) => `${params.row.name}`,
    },
    ...tableHeaderList.map((headerString, index) => ({
      field: headerString,
      align: "right" as const,
      headerAlign: "right" as const,
      headerClassName: "vest-header-class",
      cellClassName: (params: GridCellParams) =>
        params.id === allGrantsString && params.rowNode.type === "pinnedRow"
          ? "all-grants-summary-row-cells"
          : firstAndLastColumnsList.includes(headerString)
          ? "summary-column-cell"
          : "regular-column-cell",
      renderHeader: (params: GridColumnHeaderParams) => {
        let firstRow = "";
        let secondRow = headerString;
        if (firstAndLastColumnsList.includes(headerString)) {
          if (firstAndLastColumnsList[0] === headerString) {
            firstRow = "held shares (value)";
            secondRow = "Current Position";
          } else {
            firstRow = "all shares (value)";
            secondRow = "Held plus Unvested";
          }
        } else {
          firstRow = vestingPrefixText(index);
          secondRow = headerString;
        }
        return (
          <Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-end" }}>
            <Typography color="text.primary" fontSize={mediumFontSize}>
              {firstRow}
            </Typography>
            <Typography sx={{ fontWeight: "bold", fontSize: bigFontSize }}>{secondRow}</Typography>
          </Box>
        );
      },
      // headerName:
      // (firstAndLastColumnsList.includes(headerString)
      //   ? ""
      //   : incrementalOrCumulative === "Incremental"
      //   ? uniqueVestDates[index - 1].isBefore(
      //       today,
      //       // @ts-expect-error
      //       groupDatesBySetting
      //     )
      //     ? "Vested " + (groupDatesBySetting === "day" ? "on" : "in")
      //     : "Vesting " + (groupDatesBySetting === "day" ? "on" : "in")
      //   : uniqueVestDates[index - 1].isBefore(
      //       today,
      //       // @ts-expect-error
      //       groupDatesBySetting
      //     )
      //   ? "Vested by"
      //   : "Vesting by") +
      // " " +
      // headerString,
      minWidth: firstAndLastColumnsList.includes(headerString) ? firstAndLastColumnsListWidth : 80,
      sortable: false,
      renderCell: (params: GridRenderCellParams) => {
        return dateRowCellRender(params, index);
      },
    })),
  ];

  const dataGridAutosizeOptions = {
    includeOutliers: true,
    includeHeaders: true,
  };

  // React.useEffect(() => {
  //   // if (!loadingTable) {
  //   setLoadingTable(true);
  //   setTimeout(() => {
  //     dataGridApiRef.current.autosizeColumns(dataGridAutosizeOptions);
  //     setLoadingTable(false);
  //   }, 500); // TODO: fix this delay hack as a table call back instead
  //   // }
  // }, [groupDatesBySetting]); //TODO: figure out how to autosize columns better than this

  // TODO: this doesn't hide the history columns if there aren't enough columns to the right
  // TODO: make this only fire once on load?
  // TODO: start scrolling with todays date at the front
  // React.useEffect(() => {
  //   const maxColIndex =
  //     gridColumnDefinitionsSelector(dataGridApiRef).length - 1;
  //   if (maxColIndex > 2) {
  //     dataGridApiRef.current.scrollToIndexes({
  //       rowIndex: 0,
  //       colIndex: maxColIndex, // all the way to the end
  //     }); // TODO: change this to jump to today button

  //     const numDatesBeforeToday = uniqueVestDates
  //       .map((dt) =>
  //         dt.isBefore(
  //           today,
  //           // @ts-expect-error
  //           groupDatesBySetting,
  //         ),
  //       )
  //       .filter(Boolean).length;

  //     dataGridApiRef.current.scrollToIndexes({
  //       rowIndex: 0,
  //       colIndex: Math.min(numDatesBeforeToday + 2, maxColIndex),
  //     });
  //   }
  // }, [dataGridApiRef, uniqueVestDates]);

  const periodToggle = (
    <ToggleButtonGroup // TODO: fix the spacing on this?
      value={groupDatesBySetting}
      exclusive
      onChange={(e, newValue) => (newValue ? setGroupDatesBySetting(newValue) : null)}
      sx={{
        px: paperPadding,
        display: "flex",
        flexGrow: 1,
        flexBasis: 0,
        justifyContent: isMobile ? "center" : "flex-start",
      }}
    >
      {GranularityArray.map((period, index) => (
        <Tooltip
          key={index}
          title={`Group vesting events in ${period.toLowerCase()}s.`}
          placement="top-start"
          arrow
          enterDelay={enterToolTipDelay}
        >
          <ToggleButton value={period} selected={groupDatesBySetting === period}>
            {period}
          </ToggleButton>
        </Tooltip>
      ))}
    </ToggleButtonGroup>
  );

  const taxToggle = props.firstWithholdingCheckDone ? (
    <FormGroup sx={{ alignSelf: "center" }}>
      <FormControlLabel
        control={<Switch checked={props.showNet} onChange={() => props.setShowNet((old) => !old)} />}
        disabled={!props.withholdingOn}
        label={
          props.withholdingOn ? (
            <LearnTextEmbed
              content={
                "Show values net of your configured " +
                (props.withholdingRate >= 0
                  ? props.withholdingRate.toLocaleString("en-US", {
                      style: "percent",
                      maximumFractionDigits: 0,
                    }) + " "
                  : "") +
                "tax withholding"
              }
            />
          ) : (
            <LearnTextEmbed content="Enable tax withholding rate in your profile to unlock net values" />
          )
        }
      />
    </FormGroup>
  ) : (
    <Skeleton width={200} />
  );

  const incrementalOrCumulativeToggle = (
    <ToggleButtonGroup
      value={incrementalOrCumulative}
      exclusive
      onChange={(e, newValue) => (newValue ? setIncrementalOrCumulative(newValue) : null)}
      sx={{
        px: paperPadding,
        display: "flex",
        flexGrow: 1,
        flexBasis: 0,
        justifyContent: isMobile ? "center " : "flex-end",
      }}
    >
      {[
        {
          value: "Incremental",
          tip: "Displays the incremental shares and/or value vested or vesting in each period.",
        },
        {
          value: "Cumulative",
          tip: "Displays the sum total shares and/or value vested at each point in time.",
        },
      ].map((option, index) => (
        <Tooltip key={index} title={option.tip} placement="top-start" arrow enterDelay={enterToolTipDelay}>
          <ToggleButton value={option.value} selected={incrementalOrCumulative === option.value}>
            {option.value}
          </ToggleButton>
        </Tooltip>
      ))}
    </ToggleButtonGroup>
  );

  return (
    <Paper sx={{ padding: paperPadding, position: "relative" }} className="highlight-for-details-tour-step-2">
      {/*Have to import these weights to style the table header*/}
      <ThemeProvider theme={deepmerge(theme, removeDefaultGrayBackgroundTheme)}>
        <DataGridPro
          className="tour-step-1"
          apiRef={dataGridApiRef}
          slots={{
            footer: CustomFooterAddButtonComponent,
          }}
          slotProps={{
            footer: { onClick: props.startAdd, loading: props.loadingAddStockPlan },
          }}
          disableColumnMenu
          loading={props.loadingTable} // TODO: set up a loading wheel when loading
          sx={dataGridSX}
          getRowId={(row) => row.name}
          rows={incrementalOrCumulative === "Incremental" ? rowsNoTotal : rowsNoTotalCumulative} //TODO: put the incremental/cumulative data into one rows object
          pinnedRows={{
            top: incrementalOrCumulative === "Incremental" ? rowsTotal : rowsTotalCumulative,
          }}
          initialState={{
            pinnedColumns: {
              left: ["rowNames", firstAndLastColumnsList[0]],
              right: [firstAndLastColumnsList[1]],
            },
          }}
          columns={dataGridColumns}
          // TODO: do we want column groups?
          // experimentalFeatures={{ columnGrouping: true }}
          // columnGroupingModel={[
          //   { groupId: "asdf", children: [{ field: "rowNames" }] },
          // ]}
          autosizeOptions={dataGridAutosizeOptions}
        />
      </ThemeProvider>
      {/* TODO: make it so that you can uncheck these from total values?*/}
      <Box
        sx={{
          display: "flex",
          justifyContent: isMobile ? null : "center",
          flexDirection: isMobile ? "column" : "row",
          width: "100%",
          pt: paperPadding,
          pb: 1,
          gap: isMobile ? 1 : 0,
          overflowX: isMobile ? "auto" : "visible",
        }}
      >
        {/* <ToggleButtonGroup
          value={props.displayFields}
          onChange={(e, newValue) => {
            if (newValue.length > 0) {
              props.setDisplayFields(displayFieldsOrder.filter((field) => newValue.includes(field)));
            }
          }}
          sx={{
            px: paperPadding,
            display: "flex",
            flexGrow: 1,
            flexBasis: 0,
            justifyContent: "flex-start",
          }}
        >
          {displayFieldsOrder.map((text, index) => (
            <Tooltip
              key={index}
              title={
                text === "Value" && props.stockPrice === -1
                  ? `Awaiting ${text.toLowerCase()} data...`
                  : `Displays vesting values as ${text.toLowerCase()}.`
              }
              placement="top-start"
              arrow
              enterDelay={enterToolTipDelay}
            >
              <ToggleButton
                value={text}
                disabled={text === "Value" && props.stockPrice === -1}
                selected={props.displayFields.includes(text)}
              >
                {text}
              </ToggleButton>
            </Tooltip>
          ))}
        </ToggleButtonGroup> */}
        {isMobile ? (
          <>
            {taxToggle}
            {periodToggle}
            {incrementalOrCumulativeToggle}
          </>
        ) : (
          <>
            {periodToggle}
            {taxToggle}
            {incrementalOrCumulativeToggle}
          </>
        )}
      </Box>
      <AsOf referenceDateString={props.referenceDateString} />
    </Paper>
  );
}

export default DetailsTable;
